import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {Subject, takeUntil} from 'rxjs';
import * as fromRoot from '../../../../../state/app.state';
import * as FoodGuideActions from '../../core/food-guide.actions';
import * as FoodGuideSelectors from '../../core/food-guide.selectors';
import {FoodGuideItem, FoodGuideAuditEntry} from '../../model/food-guide.model';
import {FetchFoodCategoriesRequest} from '../../core/food-guide.actions';

@Component({
  selector: 'app-food-guide-audit',
  templateUrl: './food-guide-audit.component.html',
  styleUrls: ['./food-guide-audit.component.css']
})
export class FoodGuideAuditComponent implements OnInit, OnDestroy {
  @Input() data: any;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  item: FoodGuideItem;
  auditList: FoodGuideAuditEntry[] = [];
  loading = false;
  totalItems = 0;
  pageNumber = 1;
  hasNext = false;
  isFirstPage = true;
  limit = 10;

  categoryMap: {[key: number]: string} = {};

  trackedFields = [
    {key: 'FG ID', oldField: 'oldFgId', newField: 'newFgId', altOldField: 'old_fg_id', altNewField: 'new_fg_id'},
    {key: 'Name', oldField: 'oldName', newField: 'newName'},
    {key: 'Search Terms', oldField: 'oldSearchTerms', newField: 'newSearchTerms'},
    {key: 'Color', oldField: 'oldColor', newField: 'newColor'},
    {key: 'Category', oldField: 'oldCategory', newField: 'newCategory', resolveMap: 'categoryMap'},
    {key: 'Serving Amount', oldField: 'oldServingAmount', newField: 'newServingAmount'},
    {key: 'Serving Unit', oldField: 'oldServingUnit', newField: 'newServingUnit'},
    {key: 'Active', oldField: 'oldIsActive', newField: 'newIsActive'},
  ];

  actionColors: any = {
    'CREATE': '#28A745',
    'UPDATE': '#0070E0',
    'RETIRE': '#DC3545',
    'UNRETIRE': '#17A2B8',
  };

  constructor(private store: Store<fromRoot.State>) {}

  ngOnInit(): void {
    this.item = this.data?.data?.item;

    this.store.select(FoodGuideSelectors.foodCategories)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(categories => {
        this.categoryMap = {};
        for (const cat of (categories || [])) {
          this.categoryMap[cat.key] = cat.value;
        }
      });

    this.store.dispatch(FetchFoodCategoriesRequest());

    this.store.select(FoodGuideSelectors.auditHistory)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.loading = data.loading;
        this.totalItems = data.total;
        this.hasNext = data.hasNext;

        if (data.pageNumber === 1) {
          this.auditList = data.list || [];
        } else {
          this.auditList = [...this.auditList, ...(data.list || [])];
        }
      });

    this.fetchHistory();
  }

  fetchHistory(): void {
    this.store.dispatch(FoodGuideActions.FetchFoodGuideHistoryRequest({
      payload: {
        id: this.item.id,
        pageNumber: this.pageNumber,
        limit: this.limit,
      }
    }));
  }

  onScroll(): void {
    if (!this.loading && this.hasNext) {
      this.pageNumber++;
      this.isFirstPage = false;
      this.fetchHistory();
    }
  }

  getChangedFields(entry: FoodGuideAuditEntry): any[] {
    const changes: any[] = [];
    for (const field of this.trackedFields) {
      const oldVal = entry[field.oldField] ?? (field['altOldField'] ? entry[field['altOldField']] : undefined);
      const newVal = entry[field.newField] ?? (field['altNewField'] ? entry[field['altNewField']] : undefined);
      if (oldVal !== newVal && !(oldVal == null && newVal == null)) {
        let displayOld = oldVal != null ? String(oldVal) : '(none)';
        let displayNew = newVal != null ? String(newVal) : '(none)';
        if (field['resolveMap'] && this[field['resolveMap']]) {
          displayOld = oldVal != null ? (this[field['resolveMap']][oldVal] || String(oldVal)) : '(none)';
          displayNew = newVal != null ? (this[field['resolveMap']][newVal] || String(newVal)) : '(none)';
        }
        changes.push({
          label: field.key,
          oldValue: displayOld,
          newValue: displayNew,
        });
      }
    }
    return changes;
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
